/**
 * The one place a failed write is reported (BEN-141 FP-103).
 *
 * Every `useMutation` declares `meta.write` (`@fomofi/shared`'s
 * `WriteFailureMeta`), and the QueryClient's MutationCache calls
 * `reportWriteFailure` for every failure. A "global" write gets a toast in the
 * shared words; an "inline" write is the call site's to report, and nothing is
 * said here, so no failure is ever reported twice. Call sites do not toast write
 * failures themselves. The contract is followups 2026-10-06-74.
 *
 * A mutation that declares nothing is not reported here. That is deliberate
 * while the per-area passes migrate the existing per-site toasts (reporting it
 * here as well would say everything twice); `writeFailureGuard.test.ts` keeps
 * the undeclared list from growing.
 */
import type { Mutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { writeFailureCopy, type WriteFailureMeta } from "@fomofi/shared";

import { LegalAcceptanceRequired, SubscriptionRequired } from "@/api/errors";
import { queryFailureKind } from "@/lib/queryFailure";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: { write?: WriteFailureMeta };
  }
}

/**
 * Refusals that already own a screen are not failed writes: the paywall, the
 * legal-acceptance dialog, and an ended session (the API client has already
 * sent the user to sign in). Saying "Couldn't save" over any of them would
 * report a working app as broken.
 */
export function isReportableWriteFailure(error: unknown): boolean {
  if (error instanceof SubscriptionRequired) return false;
  if (error instanceof LegalAcceptanceRequired) return false;
  return queryFailureKind(error) !== "session-expired";
}

export function reportWriteFailure(
  error: unknown,
  _variables: unknown,
  _context: unknown,
  mutation: Pick<Mutation<unknown, unknown, unknown, unknown>, "meta">,
): void {
  const write = mutation.meta?.write;
  if (!write || write.report !== "global") return;
  if (!isReportableWriteFailure(error)) return;
  const { title, body } = writeFailureCopy(write, error);
  // The id folds repeats into one toast: a double-tap on a failing write is one failure to the user.
  toast.error(title, { description: body, id: `write-failure:${title}|${body}` });
}
